import { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { FileDropzone, Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui';
import type { QueuedFile } from '@/components/ui';
import type { AIExtractionJob, ExtractedMenuItem, AIImportOptions } from '@/types/api';
import {
  ArrowLeft,
  Sparkles,
  Check,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Edit2,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { RestaurantTitleSuffix } from '@/components/restaurant/RestaurantTitleSuffix';

type PageState = 'upload' | 'processing' | 'review';

export default function AIMenuUploadPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numericRestaurantId = Number(restaurantId);

  const [pageState, setPageState] = useState<PageState>('upload');
  const [files, setFiles] = useState<QueuedFile[]>([]);
  const [targetCategory, setTargetCategory] = useState<string>('');
  const [validFrom, setValidFrom] = useState<string>('');
  const [validTo, setValidTo] = useState<string>('');
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const { data: categories } = useQuery({
    queryKey: ['menuCategories', numericRestaurantId],
    queryFn: () => apiClient.getMenuCategories(numericRestaurantId),
    enabled: !!numericRestaurantId,
  });

  const { data: extractionJob } = useQuery({
    queryKey: ['aiExtractionJob', numericRestaurantId, currentJobId],
    queryFn: () => apiClient.getAIExtractionJob(numericRestaurantId, currentJobId!),
    enabled: !!currentJobId && pageState === 'processing',
    refetchInterval: (query) => {
      const job = query.state.data;
      if (job?.status === 'COMPLETED' || job?.status === 'FAILED') {
        return false;
      }
      return 2000;
    },
  });

  const startExtractionMutation = useMutation({
    mutationFn: () => {
      const fileObjects = files.map((f) => f.file);
      return apiClient.startAIExtraction(numericRestaurantId, fileObjects, {
        targetCategory: targetCategory || undefined,
        validFrom: validFrom || undefined,
        validTo: validTo || undefined,
      });
    },
    onSuccess: (job) => {
      setCurrentJobId(job.id);
      setPageState('processing');
    },
  });

  const importMutation = useMutation({
    mutationFn: (options: { itemIds: string[]; options: AIImportOptions }) =>
      apiClient.importAIExtractedItems(
        numericRestaurantId,
        currentJobId!,
        options.itemIds,
        options.options
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems', numericRestaurantId] });
      navigate(`/restaurants/${restaurantId}/menu`);
    },
  });

  // Handle job completion
  if (extractionJob?.status === 'COMPLETED' && pageState === 'processing') {
    setPageState('review');
  }

  const handleFilesChange = useCallback((newFiles: QueuedFile[]) => {
    setFiles(newFiles);
  }, []);

  const handleStartExtraction = () => {
    if (files.length === 0) {
      return;
    }
    startExtractionMutation.mutate();
  };

  const renderUploadState = () => (
    <div className="space-y-6">
      <div className="card p-6">
        <FileDropzone
          files={files}
          onFilesChange={handleFilesChange}
          accept={['image/png', 'image/jpeg', 'application/pdf']}
          maxSize={25 * 1024 * 1024}
          maxFiles={10}
        />
      </div>

      {files.length > 0 && (
        <div className="card p-6 space-y-4">
          <div>
            <label htmlFor="target-category" className="block text-sm font-medium text-gray-700 mb-1">
              Target Category (optional)
            </label>
            <select
              id="target-category"
              value={targetCategory}
              onChange={(e) => setTargetCategory(e.target.value)}
              className="input w-full"
            >
              <option value="">Auto-detect from menu</option>
              {categories?.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Menu Validity Period (for seasonal menus)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                className="input"
                placeholder="Start date (opt.)"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
                className="input"
                placeholder="End date (opt.)"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => navigate(`/restaurants/${restaurantId}/menu`)}
              className="btn btn-outline h-10"
            >
              Cancel
            </button>
            <button
              onClick={handleStartExtraction}
              disabled={files.length === 0 || startExtractionMutation.isPending}
              className="btn btn-primary h-10"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {startExtractionMutation.isPending ? 'Starting...' : 'Extract Menu Items'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderProcessingState = () => (
    <div className="card p-8 text-center">
      <div className="max-w-md mx-auto">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="h-8 w-8 text-primary-600 animate-pulse" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {extractionJob?.status === 'FAILED' ? 'Extraction Failed' : 'Extracting Menu Items...'}
        </h2>

        {extractionJob?.status === 'FAILED' ? (
          <div className="space-y-4">
            <p className="text-gray-600">
              We encountered an error processing your files. Please try again.
            </p>
            {extractionJob.errors && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-left">
                <p className="text-sm text-red-700">
                  {extractionJob.errors.join(', ')}
                </p>
              </div>
            )}
            <button
              onClick={() => {
                setPageState('upload');
                setCurrentJobId(null);
              }}
              className="btn btn-primary h-10"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              AI is analyzing your menu images. This may take a few moments.
            </p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progress</span>
                <span>
                  {extractionJob?.processedPages || 0} / {extractionJob?.totalPages || files.length} pages
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${extractionJob?.progress || 0}%` }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderReviewState = () => {
    if (!extractionJob) {
      return null;
    }
    return (
      <ReviewInterface
        job={extractionJob}
        onBack={() => {
          setPageState('upload');
          setCurrentJobId(null);
          setFiles([]);
        }}
        onImport={() => setShowImportModal(true)}
      />
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to={`/restaurants/${restaurantId}/menu`}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            AI Menu Import
            <RestaurantTitleSuffix restaurantId={numericRestaurantId} />
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Upload menu images or PDFs and let AI extract the items
          </p>
        </div>
      </div>

      {pageState === 'upload' && renderUploadState()}
      {pageState === 'processing' && renderProcessingState()}
      {pageState === 'review' && renderReviewState()}

      {showImportModal && extractionJob && (
        <ImportOptionsModal
          job={extractionJob}
          onClose={() => setShowImportModal(false)}
          onImport={(itemIds, options) => {
            importMutation.mutate({ itemIds, options });
          }}
          isImporting={importMutation.isPending}
        />
      )}
    </div>
  );
}

interface ReviewInterfaceProps {
  job: AIExtractionJob;
  onBack: () => void;
  onImport: () => void;
}

function ReviewInterface({ job, onBack, onImport }: ReviewInterfaceProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(
    new Set(job.extractedItems.filter((i) => i.selected).map((i) => i.id))
  );
  const [editingItem, setEditingItem] = useState<ExtractedMenuItem | null>(null);

  const totalPages = job.totalPages;
  const totalSelectedItems = selectedItems.size;

  const averageConfidence =
    job.extractedItems.length > 0
      ? Math.round(
          job.extractedItems.reduce((sum, i) => sum + i.confidence, 0) /
            job.extractedItems.length
        )
      : 0;

  const toggleItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Review Extracted Items</h2>
        <span className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left side - Image viewer */}
        <div className="card p-4">
          <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center mb-4 overflow-hidden">
            <div
              style={{ transform: `scale(${zoom / 100})` }}
              className="transition-transform"
            >
              <div className="text-gray-500 text-center">
                <Upload className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>Menu page {currentPage} preview</p>
                <p className="text-xs">(Image preview would appear here)</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn btn-outline h-8 px-3"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-outline h-8 px-3"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Zoom:</span>
              <button
                onClick={() => setZoom((z) => Math.max(50, z - 25))}
                className="btn btn-outline h-8 w-8 p-0"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <input
                type="range"
                min={50}
                max={200}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-24"
              />
              <button
                onClick={() => setZoom((z) => Math.min(200, z + 25))}
                className="btn btn-outline h-8 w-8 p-0"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Right side - Extracted items */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">
              EXTRACTED ITEMS ({job.extractedItems.length})
            </h3>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {job.extractedItems.map((item) => (
              <ExtractedItemCard
                key={item.id}
                item={item}
                isSelected={selectedItems.has(item.id)}
                onToggle={() => toggleItem(item.id)}
                onEdit={() => setEditingItem(item)}
              />
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Confidence:</span>
              <div className="flex items-center gap-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      averageConfidence >= 80
                        ? 'bg-green-500'
                        : averageConfidence >= 60
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${averageConfidence}%` }}
                  />
                </div>
                <span className="font-medium">{averageConfidence}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="btn btn-outline h-10">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Upload
        </button>
        <button
          onClick={onImport}
          disabled={totalSelectedItems === 0}
          className="btn btn-primary h-10"
        >
          Review Complete - Import {totalSelectedItems} Items
          <ChevronRight className="h-4 w-4 ml-2" />
        </button>
      </div>

      {editingItem && (
        <EditItemModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={() => {
            // In a real app, this would update the item in state
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
}

interface ExtractedItemCardProps {
  item: ExtractedMenuItem;
  isSelected: boolean;
  onToggle: () => void;
  onEdit: () => void;
}

function ExtractedItemCard({ item, isSelected, onToggle, onEdit }: ExtractedItemCardProps) {
  return (
    <div
      className={`border rounded-lg p-3 transition-colors ${
        isSelected
          ? 'border-primary-300 bg-primary-50'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggle}
            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <div>
            <div className="flex items-center gap-2">
              {item.isNew ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  NEW
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  UPDATE
                </span>
              )}
              <h4 className="font-medium text-gray-900">{item.name}</h4>
            </div>
            {item.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="text-gray-900 font-medium">
                £{item.price.toFixed(2)}
              </span>
              <span className="text-gray-500">Category: {item.category}</span>
            </div>
            {item.changes && item.changes.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                {item.changes.map((change, idx) => (
                  <span key={idx}>
                    {change.field}: {String(change.oldValue)} → {String(change.newValue)}
                    {idx < item.changes!.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onEdit}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="Edit item"
        >
          <Edit2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

interface ImportOptionsModalProps {
  job: AIExtractionJob;
  onClose: () => void;
  onImport: (itemIds: string[], options: AIImportOptions) => void;
  isImporting: boolean;
}

function ImportOptionsModal({ job, onClose, onImport, isImporting }: ImportOptionsModalProps) {
  const [mode, setMode] = useState<AIImportOptions['mode']>('SMART_MERGE');
  const [syncImmediately, setSyncImmediately] = useState(true);

  const newItems = job.extractedItems.filter((i) => i.isNew);
  const updateItems = job.extractedItems.filter((i) => !i.isNew);

  const handleImport = () => {
    const selectedItemIds = job.extractedItems.filter((i) => i.selected).map((i) => i.id);
    onImport(selectedItemIds, {
      mode,
      syncImmediately,
      itemsToKeep: [],
      itemsToDelete: [],
    });
  };

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>Import Options</ModalHeader>
      <ModalBody>
        <p className="text-gray-600 mb-4">
          How should we handle these {job.extractedItems.length} items?
        </p>

        <div className="space-y-3">
          <label
            className={`block p-4 border rounded-lg cursor-pointer transition-colors ${
              mode === 'ADD_ONLY'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="importMode"
                checked={mode === 'ADD_ONLY'}
                onChange={() => setMode('ADD_ONLY')}
                className="mt-1 h-4 w-4 text-primary-600"
              />
              <div>
                <span className="font-medium text-gray-900">Add Only</span>
                <p className="text-sm text-gray-600 mt-1">
                  Add {newItems.length} new items. Skip {updateItems.length} that match existing items.
                </p>
              </div>
            </div>
          </label>

          <label
            className={`block p-4 border rounded-lg cursor-pointer transition-colors ${
              mode === 'SMART_MERGE'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="importMode"
                checked={mode === 'SMART_MERGE'}
                onChange={() => setMode('SMART_MERGE')}
                className="mt-1 h-4 w-4 text-primary-600"
              />
              <div>
                <span className="font-medium text-gray-900">
                  Smart Merge
                  <span className="ml-2 text-xs text-primary-600 font-normal">Recommended</span>
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  Add {newItems.length} new items + Update {updateItems.length} existing items with
                  new prices and descriptions.
                </p>
              </div>
            </div>
          </label>

          <label
            className={`block p-4 border rounded-lg cursor-pointer transition-colors ${
              mode === 'REPLACE_CATEGORY'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="importMode"
                checked={mode === 'REPLACE_CATEGORY'}
                onChange={() => setMode('REPLACE_CATEGORY')}
                className="mt-1 h-4 w-4 text-primary-600"
              />
              <div>
                <span className="font-medium text-gray-900">Replace Category</span>
                <p className="text-sm text-gray-600 mt-1">
                  Replace all items in the detected category with these {job.extractedItems.length} items.
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  Some existing items may be removed
                </p>
              </div>
            </div>
          </label>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={syncImmediately}
              onChange={(e) => setSyncImmediately(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              Sync changes to POS terminals immediately
            </span>
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <button onClick={onClose} className="btn btn-outline h-10">
          Cancel
        </button>
        <button
          onClick={handleImport}
          disabled={isImporting}
          className="btn btn-primary h-10"
        >
          {isImporting ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              Import {job.extractedItems.filter((i) => i.selected).length} Items
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}

interface EditItemModalProps {
  item: ExtractedMenuItem;
  onClose: () => void;
  onSave: (item: ExtractedMenuItem) => void;
}

function EditItemModal({ item, onClose, onSave }: EditItemModalProps) {
  const [formData, setFormData] = useState({
    name: item.name,
    description: item.description || '',
    price: item.price,
    category: item.category,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...item,
      ...formData,
    });
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>Edit Item</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label htmlFor="item-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                id="item-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label htmlFor="item-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="item-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input w-full"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="item-price" className="block text-sm font-medium text-gray-700 mb-1">
                  Price (£)
                </label>
                <input
                  id="item-price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })
                  }
                  className="input w-full"
                />
              </div>
              <div>
                <label htmlFor="item-category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <input
                  id="item-category"
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-outline h-10">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary h-10">
            Save Changes
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
